import { DATE_FORMATS_FNS } from "components";
import { TYRE_POSITIONS } from "models";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { TFunction } from "i18next";
import type { TYRE_REPLACEMENT_SOURCES, Tyre, TyreBrandDiscount, TyreDiscount, VehicleTyre, VehicleTyreReplacement } from "models";
import type { ReplacementOffer } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/hooks/useTyreReplacementOffer";
import type { ReplacementOrder } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOrderModal/hooks";

export const getTyreDescription = (tyre?: Tyre) => {
  if (!tyre) return "";

  return !tyre.description.includes(`${tyre.carry_capacity}/${tyre.speed}`) && !tyre.description.includes(`${tyre.carry_capacity}${tyre.speed}`)
    ? `${tyre.description} ${tyre.carry_capacity}${tyre.speed}`
    : tyre.description;
};

const normalizeTyreDescription = (tyre: Tyre) => {
  const carryCapacitySpeed = `${tyre.carry_capacity}${tyre.speed}`;
  const widthHeight = `${tyre.width}/${tyre.height}`;

  if (!tyre.description.includes(widthHeight)) return `${widthHeight} R${tyre.size} ${tyre.description} ${carryCapacitySpeed}`;

  const modelName = tyre.description
    .split(" ")
    .filter(t => {
      return (
        t.toLowerCase() !== tyre.manufacturer.toLowerCase() &&
        !t.includes(widthHeight) &&
        !t.includes(`R${tyre.size}`) &&
        !t.includes(carryCapacitySpeed) &&
        !t.includes(`${tyre.carry_capacity}/`)
      );
    })
    .join(" ");

  if (modelName) return `${widthHeight} R${tyre.size} ${modelName} ${carryCapacitySpeed}`;
  return `${widthHeight} R${tyre.size} ${carryCapacitySpeed}`;
};

export const getFullTyreDescription = (tyre?: Tyre, isTyreNet?: boolean) => {
  if (!tyre) return "";
  if (isTyreNet) return normalizeTyreDescription(tyre);
  return getTyreDescription(tyre);
};

export const getTyreSize = (tyre?: Tyre) => {
  if (!tyre) return "";
  return `${tyre.width}-${tyre.height}-R${tyre.size}`;
};

const tyreToTyreOffer = (source: TYRE_REPLACEMENT_SOURCES, tyre: Tyre, discount: TyreDiscount | TyreBrandDiscount | null): ReplacementOffer => {
  const offer: ReplacementOffer = {
    ...tyre,
    discount: null,
    net_margin_euro: null,
    net_margin_percent: null,
    price: tyre.gross_price ?? 0,
    priceOnOtherPosition: 0,
    quantity: 0,
    selected: false,
    source
  };

  if (!discount) return offer;

  if (discount.discount) {
    offer.discount = discount.discount;
    offer.price = (tyre.gross_price ?? 0) * (1 - discount.discount / 100);

    return offer;
  }

  if (discount.net_margin_euro) {
    offer.net_margin_euro = discount.net_margin_euro;
    offer.price = (tyre.net_price ?? 0) + discount.net_margin_euro;

    return offer;
  }

  if (discount.net_margin_percent) {
    offer.net_margin_percent = discount.net_margin_percent;
    offer.price = (tyre.net_price ?? 0) * (1 + discount.net_margin_percent / 100);

    return offer;
  }

  return offer;
};

export const tyresToReplacementOffers = (source: TYRE_REPLACEMENT_SOURCES, tyres: Tyre[], discount: TyreDiscount | null): ReplacementOffer[] => {
  return tyres.map(tyre => {
    if (discount?.tyre_brand_discounts?.length) {
      const brandDiscount = discount.tyre_brand_discounts.find(bd => bd.active && bd.brand.toLowerCase() === tyre.manufacturer.toLowerCase());
      if (brandDiscount) return tyreToTyreOffer(source, tyre, brandDiscount);
    }

    return tyreToTyreOffer(source, tyre, discount);
  });
};

export const replacementOffersToVehicleTyreReplacements = (tyreOffers: ReplacementOffer[], appointment_id: number, tyre: VehicleTyre): VehicleTyreReplacement[] => {
  return tyreOffers.map(to => ({
    id: -1,
    created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
    updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
    price: to.price,
    discount: to.discount,
    net_margin_euro: to.net_margin_euro,
    net_margin_percent: to.net_margin_percent,
    source: to.source,
    remark: "",
    selected: false,
    mounted: false,
    tyre_team_system_number: to.tyre_team_system_number ?? null,
    tyre_team_channel: to.tyre_team_channel ?? null,
    delivery_method: to.tyre_team_delivery_method ?? "",
    order_placed: false,
    visible_to_customer: true,
    tyre_id: to.id,
    location: tyre.location,
    axle: tyre.axle,
    position: tyre.position,
    appointment_id,
    intervention_id: null,
    tyre: { ...to }
  }));
};

const tyreReplacementMatchesTyreOrder = (order: ReplacementOrder, replacement: VehicleTyreReplacement, isTyreTeamEnabled: boolean) =>
  isTyreTeamEnabled
    ? order.tyre_team_system_number &&
      order.tyre_team_system_number === replacement.tyre_team_system_number &&
      order.tyre_team_channel === replacement.tyre_team_channel &&
      order.delivery_method === replacement.delivery_method
    : order.tyre?.ean && order.tyre.ean === replacement.tyre?.ean && order.delivery_method === replacement.delivery_method;

export const getReplacementsNotOrdered = (allReplacements: VehicleTyreReplacement[], isTyreTeamEnabled: boolean) =>
  allReplacements.filter(replacement =>
    isTyreTeamEnabled ? replacement.tyre_team_system_number && !replacement.order_placed : replacement.tyre?.ean && !replacement.order_placed
  );

export const tyreReplacementsToOrders = (replacements: VehicleTyreReplacement[], isTyreTeamEnabled: boolean): ReplacementOrder[] => {
  const orders: ReplacementOrder[] = [];

  getReplacementsNotOrdered(replacements, isTyreTeamEnabled).forEach(replacement => {
    const existingIdx = orders.findIndex(item => tyreReplacementMatchesTyreOrder(item, replacement, isTyreTeamEnabled));

    if (existingIdx < 0)
      return orders.push({
        ...replacement,
        quantity: replacement.selected ? 1 : 0,
        not_ordered_yet_quantity: replacement.order_placed ? 0 : 1
      });

    if (!replacement.order_placed) orders[existingIdx].not_ordered_yet_quantity += 1;
    if (replacement.selected) orders[existingIdx].quantity += 1;
  });

  return orders;
};

export const hasQuantityExceedingStock = (stock: string = "0", quantity: number) => {
  if (quantity === 0) return false;
  if (stock.includes(">")) return false;

  const intStock = parseInt(stock);
  if (Number.isNaN(intStock)) return false;

  return quantity > intStock;
};

export const getAxleName = (tyre: VehicleTyre | VehicleTyreReplacement, truckSpareIndex: number, t: TFunction) => {
  if (tyre.position === TYRE_POSITIONS.TruckSpare) return `${t("v8_spare_tyre", "Spare Tyre")} ${tyre.axle - truckSpareIndex + 1}`;

  return `${t("v8_axle", "Axle")} ${tyre.axle}`;
};

export const getSpareIndex = (tyresByAxle: Map<number, VehicleTyre[]>) => {
  let truckSpareIndex = -1;
  for (const [axle, tyres] of tyresByAxle) {
    if (tyres.some(t => t.position === TYRE_POSITIONS.TruckSpare)) {
      truckSpareIndex = axle;
      break;
    }
  }

  return truckSpareIndex;
};

export const sortAndGroupByAxle = (tyres: VehicleTyre[]) =>
  Map.groupBy(
    tyres.sort((a, b) => {
      if (a.axle !== b.axle) return a.axle - b.axle;
      if (a.position === TYRE_POSITIONS.TruckExteriorRight) return 1;
      if (b.position === TYRE_POSITIONS.TruckExteriorRight) return -1;

      return a.position - b.position;
    }),
    t => t.axle
  );
